import {
    faChevronDown,
    faChevronRight,
    faFloppyDisk,
    faLayerGroup,
    faRocket,
    faTrash,
    faCopy,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useEffect, useMemo, useState } from 'react'
import { ActionButton } from '~/team_tools/components/ActionButton'
import { TemplateFormFields } from './TemplateForm'
import { formFrom, parseFieldErrors, payloadOf } from './templateFormState'
import { MACHINE_TYPES, Options, Template } from './templatesApi'

export const TemplateCard = ({
    template,
    options,
    canEdit,
    expanded,
    onToggle,
    onSave,
    count,
    onCountChange,
    region,
    onRegionChange,
    machineType,
    onMachineTypeChange,
    onDuplicate,
    onDeploy,
    onDelete,
    confirmSlot,
    dragHandle,
    dragging,
}: any) => {
    const t: Template = template
    const opts: Options = options

    // The same fields as the create panel, through the same component. Editing
    // and creating a template used to be two different screens with two sets of
    // primitives, which is how the create form ended up with defaults that
    // contradicted the model while the card's were right.
    const original = useMemo(() => formFrom(t), [t])
    const [form, setForm] = useState(original)
    const [errors, setErrors] = useState<Record<string, string>>({})
    const [saving, setSaving] = useState(false)
    const [justSaved, setJustSaved] = useState(false)

    useEffect(() => {
        setForm(original)
        setErrors({})
    }, [original])

    // Only what actually changed goes out. The partial-update view applies the
    // keys it is given and leaves the rest alone, so two people editing
    // different fields of the same template do not overwrite each other.
    const changed = useMemo(() => {
        const next = payloadOf(form)
        const before = payloadOf(original)
        const diff: Record<string, any> = {}
        for (const key of Object.keys(next)) {
            if (JSON.stringify(next[key]) !== JSON.stringify(before[key])) diff[key] = next[key]
        }
        return diff
    }, [form, original])

    const dirty = Object.keys(changed).length > 0

    const save = async () => {
        setSaving(true)
        try {
            await onSave(changed)
            setErrors({})
            setJustSaved(true)
            setTimeout(() => setJustSaved(false), 1500)
        } catch (err: any) {
            const fields = parseFieldErrors(err?.message ?? '')
            setErrors(fields)
        } finally {
            setSaving(false)
        }
    }

    const regions = t.deploy_slot_regions ?? []

    return (
        // A real border, and the only line this card draws.
        //
        // It used to carry `border-gray-500 border-opacity-50` with no `border`
        // width class, so nothing rendered: the card had no outline at all. The
        // one visible rule was a `border-b` *inside* the card, under the name
        // and badges -- which put a line between a template's own name and its
        // own deploy controls, and none between one template and the next. Read
        // top to bottom the page then went line, controls, name, badges, line,
        // and the controls looked like they belonged to the template above.
        // Every mis-deploy this could cause is a real server built from the
        // wrong template.
        //
        // Now the box edge is the separator, because the box is the thing being
        // separated. Nothing inside divides a card against itself.
        <div
            className={`flex flex-col gap-2 p-2 rounded border transition-opacity ${
                dragging
                    ? 'opacity-40 border-violet-400/60 bg-violet-500/10'
                    : 'border-white/15 bg-black bg-opacity-20'
            }`}
        >
            {/* Three lines, not one. Name, details and every control used to
                share a single flex-nowrap row, so a long template name pushed
                the deploy controls off the right edge of the page with no way
                to reach them. Splitting them means the name can grow without
                ever moving a button. */}
            <div className='flex flex-col gap-1.5'>
                <div
                    className='flex flex-row items-start gap-2 cursor-pointer min-w-0'
                    onClick={onToggle}
                >
                    {dragHandle}
                    <FontAwesomeIcon
                        icon={expanded ? faChevronDown : faChevronRight}
                        className='opacity-40 w-3 shrink-0 mt-1'
                    />
                    <FontAwesomeIcon icon={faLayerGroup} className='shrink-0 mt-1' />
                    {/* break-words rather than truncate: the name is the one
                        thing on this card that must always be readable in
                        full, however long it is. */}
                    <span
                        className={`font-bold leading-tight break-words min-w-0 ${
                            t.enabled ? '' : 'opacity-40'
                        }`}
                    >
                        {t.name}
                    </span>
                    {!t.enabled && (
                        <small className='text-white/40 uppercase tracking-wide shrink-0 mt-0.5'>
                            disabled
                        </small>
                    )}
                </div>

                {/* Indented to line up under the name, and wrapping instead of
                    truncating: on a narrow window the map and the fee are what
                    somebody is actually reading, and an ellipsis hid them. */}
                <div
                    className={`flex flex-row flex-wrap items-center gap-1.5 text-xs uppercase tracking-wide text-white/50 ${
                        dragHandle ? 'pl-[3.25rem]' : 'pl-7'
                    }`}
                >
                    <span className='rounded bg-white/5 px-1.5 py-0.5'>
                        slots {regions.join('/') || '-'}
                    </span>
                    <span className='rounded bg-white/5 px-1.5 py-0.5'>{t.map_name}</span>
                    <span className='rounded bg-white/5 px-1.5 py-0.5'>{t.fee ?? 'free'}</span>
                    <span className='rounded bg-white/5 px-1.5 py-0.5'>
                        {t.max_players ?? '∞'} players
                    </span>
                    <span className='text-white/40 normal-case break-all'>→ {t.example_name}</span>
                </div>
            </div>

            {/* Controls on their own line, right aligned, and allowed to wrap so
                a narrow window stacks them rather than overflowing the page. */}
            <div className='flex flex-row flex-wrap items-center justify-end gap-2'>
                {/* Which region the servers land in. Shown only when there
                    is a choice to make — a single-region template deploys
                    where it always did, with no extra control to read. */}
                {regions.length > 1 && (
                    <select
                        value={region ?? regions[0]}
                        onChange={(e) => onRegionChange(e.target.value)}
                        title='Region to deploy into'
                        className='input-team-tools py-1 px-2 text-xs'
                    >
                        {regions.map((r) => (
                            <option key={r} value={r} className='bg-neutral-900'>
                                {r}
                            </option>
                        ))}
                    </select>
                )}
                {/* Which GCP family to ask for. c3 is fastest but the
                    scarcest — on 12 Aug every us-central1 zone refused it
                    and two creates failed. The workflow falls back to n2 on
                    its own; picking here skips the failed applies. */}
                <select
                    value={machineType ?? 'default'}
                    onChange={(e) => onMachineTypeChange(e.target.value)}
                    title='GCP machine type'
                    className='input-team-tools py-1 px-2 text-xs'
                >
                    {MACHINE_TYPES.map((m) => (
                        <option key={m.value} value={m.value} className='bg-neutral-900'>
                            {m.label}
                        </option>
                    ))}
                </select>
                <input
                    type='number'
                    min={1}
                    max={20}
                    value={count}
                    onChange={(e) => onCountChange(Number(e.target.value) || 1)}
                    title='How many servers to build'
                    className='input-team-tools w-14 py-1 px-2 text-xs text-center'
                />
                <ActionButton
                    onClick={onDeploy}
                    disabled={!t.enabled || !regions.length}
                    title={
                        !regions.length
                            ? 'This template has no deploy region set'
                            : t.enabled
                              ? 'Build servers from this template'
                              : 'Template is disabled'
                    }
                    className='text-green-500'
                >
                    <FontAwesomeIcon icon={faRocket} className='mr-1' /> Deploy
                </ActionButton>
                <ActionButton
                    onClick={async () => onDuplicate()}
                    disabled={!canEdit}
                    title={canEdit ? 'Copy this template into a new one' : 'Requires the executive role'}
                >
                    <FontAwesomeIcon icon={faCopy} />
                </ActionButton>
                <ActionButton onClick={onDelete} disabled={!canEdit} className='text-red-500'>
                    <FontAwesomeIcon icon={faTrash} />
                </ActionButton>
            </div>

            {confirmSlot}

            {expanded && (
                <div className='flex flex-col gap-2 text-sm'>
                    <TemplateFormFields
                        form={form}
                        setForm={setForm}
                        options={opts}
                        errors={errors}
                        disabled={!canEdit}
                    />

                    {canEdit && (
                        <div className='flex flex-row items-center justify-end gap-3'>
                            {dirty && (
                                <span className='text-xs uppercase tracking-wide opacity-50'>
                                    {Object.keys(changed).length} unsaved change
                                    {Object.keys(changed).length === 1 ? '' : 's'}
                                </span>
                            )}
                            {justSaved && (
                                <span className='text-xs uppercase tracking-wide text-green-500'>saved</span>
                            )}
                            <button
                                onClick={save}
                                disabled={!dirty || saving}
                                className='rounded bg-violet-600/80 hover:bg-violet-600 disabled:opacity-30 disabled:hover:bg-violet-600/80 px-4 py-1 text-xs font-bold uppercase tracking-wide'
                            >
                                <FontAwesomeIcon icon={faFloppyDisk} className='mr-1' />
                                {saving ? 'saving…' : 'Save'}
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}
